
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Lightbulb, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';

interface Insight {
  type: 'success' | 'warning' | 'info' | 'improvement';
  title: string;
  description: string;
  value?: string;
  trend?: 'up' | 'down' | 'stable';
  priority: 'high' | 'medium' | 'low';
}

export const SmartInsights = () => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      generateInsights();
    }
  }, [user]);

  const generateInsights = async () => {
    try {
      // Fetch all relevant data for analysis
      const [carbonData, esgData, targetData, supplierData, riskData] = await Promise.all([
        supabase.from('carbon_calculations').select('*').eq('user_id', user?.id),
        supabase.from('user_metrics_data').select(`
          *,
          metric:unified_metrics_library(*)
        `).eq('organization_id', user?.id),
        supabase.from('net_zero_targets').select('*').eq('user_id', user?.id),
        supabase.from('suppliers').select('*').eq('user_id', user?.id),
        supabase.from('risk_assessments').select('*').eq('user_id', user?.id)
      ]);

      const generatedInsights: Insight[] = [];

      // Check if user has any data
      const hasData = [carbonData.data, esgData.data, targetData.data, supplierData.data, riskData.data]
        .some(data => data && data.length > 0);

      if (!hasData) {
        generatedInsights.push({
          type: 'info',
          title: 'Get Started with Your Sustainability Journey',
          description: 'Add your first data points to unlock personalized insights and analytics.',
          priority: 'high'
        });
        setInsights(generatedInsights);
        setLoading(false);
        return;
      }

      // Carbon performance analysis
      if (carbonData.data && carbonData.data.length > 0) {
        const totalEmissions = carbonData.data.reduce((sum, item) => sum + (item.co2_equivalent || 0), 0);
        const scope1Emissions = carbonData.data
          .filter(item => item.scope === 'scope1')
          .reduce((sum, item) => sum + (item.co2_equivalent || 0), 0);
        
        if (totalEmissions > 10000) {
          generatedInsights.push({
            type: 'warning',
            title: 'High Carbon Footprint Detected',
            description: 'Your total emissions exceed 10,000 tCO₂e. Consider implementing decarbonization initiatives.',
            value: `${totalEmissions.toLocaleString()} tCO₂e`,
            trend: 'up',
            priority: 'high'
          });
        } else if (totalEmissions > 0) {
          generatedInsights.push({
            type: 'success',
            title: 'Carbon Baseline Established',
            description: 'Good progress on carbon accounting. Consider setting science-based targets.',
            value: `${totalEmissions.toLocaleString()} tCO₂e`,
            priority: 'medium'
          });
        }

        if (scope1Emissions > totalEmissions * 0.6) {
          generatedInsights.push({
            type: 'improvement',
            title: 'Focus on Direct Emissions',
            description: 'Scope 1 emissions represent a large portion. Energy efficiency could have significant impact.',
            priority: 'medium'
          });
        }
      }

      // ESG data analysis
      if (esgData.data && esgData.data.length > 0) {
        const avgScore = esgData.data.reduce((sum, item) => sum + (item.metric_value || 0), 0) / esgData.data.length;
        const validatedData = esgData.data.filter(item => item.validation_status === 'validated');
        
        if (avgScore > 80) {
          generatedInsights.push({
            type: 'success',
            title: 'Strong ESG Performance',
            description: 'Your ESG metrics show excellent performance across multiple dimensions.',
            value: `${avgScore.toFixed(1)}/100`,
            trend: 'up',
            priority: 'low'
          });
        } else if (avgScore > 60) {
          generatedInsights.push({
            type: 'info',
            title: 'Good ESG Foundation',
            description: 'Solid ESG performance with room for improvement in specific areas.',
            value: `${avgScore.toFixed(1)}/100`,
            priority: 'medium'
          });
        } else if (avgScore > 0) {
          generatedInsights.push({
            type: 'warning',
            title: 'ESG Performance Needs Attention',
            description: 'Consider developing targeted improvement plans for underperforming areas.',
            value: `${avgScore.toFixed(1)}/100`,
            trend: 'down',
            priority: 'high'
          });
        }

        if (validatedData.length < esgData.data.length * 0.5) {
          generatedInsights.push({
            type: 'improvement',
            title: 'Data Validation Opportunity',
            description: 'Many ESG data points are pending validation. This could improve credibility.',
            priority: 'medium'
          });
        }
      }

      // Net-zero targets analysis
      if (targetData.data && targetData.data.length > 0) {
        const activeTargets = targetData.data.filter(target => target.status === 'active');
        const avgProgress = activeTargets.reduce((sum, target) => sum + (target.progress_percentage || 0), 0) / activeTargets.length;
        
        if (avgProgress > 75) {
          generatedInsights.push({
            type: 'success',
            title: 'Excellent Target Progress',
            description: 'You\'re on track to achieve your net-zero commitments ahead of schedule.',
            value: `${avgProgress.toFixed(0)}% complete`,
            trend: 'up',
            priority: 'low'
          });
        } else if (avgProgress > 50) {
          generatedInsights.push({
            type: 'info',
            title: 'Good Progress on Targets',
            description: 'Steady progress toward net-zero goals. Maintain current momentum.',
            value: `${avgProgress.toFixed(0)}% complete`,
            priority: 'medium'
          });
        } else if (avgProgress > 0) {
          generatedInsights.push({
            type: 'warning',
            title: 'Target Progress Behind Schedule',
            description: 'Consider accelerating decarbonization efforts to meet your commitments.',
            value: `${avgProgress.toFixed(0)}% complete`,
            trend: 'down',
            priority: 'high'
          });
        }
      } else {
        generatedInsights.push({
          type: 'improvement',
          title: 'Set Science-Based Targets',
          description: 'Establish net-zero targets to drive systematic emissions reduction.',
          priority: 'high'
        });
      }

      // Supply chain analysis
      if (supplierData.data && supplierData.data.length > 0) {
        const assessedSuppliers = supplierData.data.filter(s => s.esg_assessment_score > 0);
        const highRiskSuppliers = supplierData.data.filter(s => s.risk_level === 'high');
        
        if (assessedSuppliers.length < supplierData.data.length * 0.7) {
          generatedInsights.push({
            type: 'improvement',
            title: 'Expand Supplier Assessments',
            description: 'Many suppliers lack ESG assessments. This represents a risk and opportunity.',
            priority: 'medium'
          });
        }

        if (highRiskSuppliers.length > 0) {
          generatedInsights.push({
            type: 'warning',
            title: 'High-Risk Suppliers Identified',
            description: `${highRiskSuppliers.length} suppliers are classified as high-risk. Consider mitigation actions.`,
            priority: 'high'
          });
        }
      }

      // Risk analysis
      if (riskData.data && riskData.data.length > 0) {
        const highRisks = riskData.data.filter(risk => risk.risk_score && risk.risk_score > 15);
        const activeRisks = riskData.data.filter(risk => risk.status === 'active');
        
        if (highRisks.length > 0) {
          generatedInsights.push({
            type: 'warning',
            title: 'High-Priority Risks Identified',
            description: `${highRisks.length} risks require immediate attention and mitigation strategies.`,
            priority: 'high'
          });
        }

        if (activeRisks.length === 0 && riskData.data.length > 0) {
          generatedInsights.push({
            type: 'success',
            title: 'Risk Management On Track',
            description: 'All identified risks have been addressed or are being actively managed.',
            priority: 'low'
          });
        }
      }

      // AI-generated recommendations based on data patterns
      const dataCategories = [
        carbonData.data?.length || 0,
        esgData.data?.length || 0,
        targetData.data?.length || 0,
        supplierData.data?.length || 0
      ];
      
      const totalDataPoints = dataCategories.reduce((sum, count) => sum + count, 0);
      
      if (totalDataPoints > 20) {
        generatedInsights.push({
          type: 'success',
          title: 'Rich Data Foundation',
          description: 'You have comprehensive sustainability data. Consider advanced analytics and reporting.',
          priority: 'low'
        });
      }

      // Seasonal and timely recommendations
      const currentMonth = new Date().getMonth();
      if (currentMonth === 11 || currentMonth === 0) { // December or January
        generatedInsights.push({
          type: 'info',
          title: 'Year-End Reporting Opportunity',
          description: 'Consider generating annual sustainability reports and setting targets for next year.',
          priority: 'medium'
        });
      }

      setInsights(generatedInsights.slice(0, 6)); // Limit to 6 insights
    } catch (error) {
      console.error('Error generating insights:', error);
      setInsights([{
        type: 'warning',
        title: 'Analysis Temporarily Unavailable',
        description: 'Unable to generate insights at this time. Please try again later.',
        priority: 'low'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'success': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'improvement': return Target;
      default: return Lightbulb;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'improvement': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-purple-600 bg-purple-50 border-purple-200';
    }
  };

  const getTrendIcon = (trend?: string) => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-3 w-3 text-red-600" />;
    return null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-12 bg-gray-300 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">AI-Powered Insights</h3>
        <Badge variant="outline" className="text-xs">Real-time Analysis</Badge>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insights.map((insight, index) => {
          const Icon = getInsightIcon(insight.type);
          const colorClass = getInsightColor(insight.type);
          
          return (
            <Card key={index} className={`border-l-4 ${colorClass}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{insight.title}</h4>
                      {insight.trend && getTrendIcon(insight.trend)}
                      <Badge 
                        variant={insight.priority === 'high' ? 'destructive' : insight.priority === 'medium' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {insight.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{insight.description}</p>
                    {insight.value && (
                      <div className="text-lg font-bold">{insight.value}</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {insights.length === 0 && (
        <Alert>
          <Lightbulb className="h-4 w-4" />
          <AlertDescription>
            No insights available yet. Add some sustainability data to unlock AI-powered recommendations and analysis.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
